/**
 * Which restricted data an authority reaches (`NXP-P8-ACCESS-POLICY-001`).
 *
 * A role grant says which operation a principal may perform. For a restricted
 * classification — personal data above all — that is deliberately not enough:
 * the decision point also requires an active ALLOW access policy naming it.
 * This screen is where those policies are drafted, put into force and retired.
 *
 * **Its own destination, not a card on Authorization.** Authorization is
 * reached on `authorization.role.read`, and the access policy administrator —
 * the one person this screen is for — is appointed by the root to hold the four
 * `authorization.access_policy.*` operations and nothing else. A card there
 * would be unreachable to exactly its holder, for the same reason Service
 * identities has its own page: a separate authority, separately appointed.
 *
 * Four operations, each asked about on its own: reading, drafting, activating
 * and deactivating. Holding one is not evidence of another.
 */

import { useCallback, useState } from "react";
import { api } from "../api/client";
import { NexusError } from "../api/errors";
import type {
  AccessPolicy,
  AccessPolicyEffect,
  AccessPolicyStatus,
  DataClassification,
  Page,
} from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { change, refusalOf, Result, type Outcome } from "../components/governed";
import {
  Banner,
  Card,
  Classification,
  Identifier,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { Empty, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

const READ = "authorization.access_policy.read";
const DRAFT = "authorization.access_policy.draft";
const ACTIVATE = "authorization.access_policy.activate";
const DEACTIVATE = "authorization.access_policy.deactivate";

const PAGE = 50;

const STATUSES: AccessPolicyStatus[] = ["draft", "active", "superseded"];

const STATUS_TONE: Record<string, Tone> = {
  draft: "wait",
  active: "ok",
  superseded: "quiet",
};

const CLASSIFICATIONS: DataClassification[] = [
  "pii",
  "phi",
  "confidential",
  "regulatory",
  "quality",
  "executive",
  "safety_restricted",
  "internal",
  "public",
];

/**
 * A refusal, said the way the platform said it.
 *
 * `FORBIDDEN_SCOPE` is the platform refusing *what the policy names* — an
 * `authorization.` surface or an appointed authority's resources, which are
 * governed by appointment and role and never by data policy. That is not the
 * caller being refused, and its sentence is shown verbatim. Everything else is
 * the shared rendering, including a `403` that means the offer went stale.
 */
function refusal(error: unknown, whatWasRefused: string): string {
  if (error instanceof NexusError && error.code === "FORBIDDEN_SCOPE") {
    return `Nexus refused: ${error.message}`;
  }
  return refusalOf(error, whatWasRefused);
}

// ── drafting ──────────────────────────────────────────────────────────────

const EMPTY_DRAFT = {
  key: "",
  effect: "allow" as AccessPolicyEffect,
  description: "",
  resource_type: "",
  operation: "",
  classification: "" as DataClassification | "",
  purpose: "",
  country: "",
  requires_multifactor: false,
};

function DraftPolicy({ onDrafted }: { onDrafted: (outcome: Outcome) => void }) {
  const [form, setForm] = useState(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);

  return (
    <Card title="Draft a policy">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          void change(
            (correlationId) =>
              // Exactly the fields the API declares; optional ones are left
              // out rather than sent empty, and the author is the session,
              // never the body. A key is safe: a retry replays.
              api.post<AccessPolicy>(
                "/authorization/access-policies",
                {
                  key: form.key.trim(),
                  effect: form.effect,
                  description: form.description.trim(),
                  resource_type: form.resource_type.trim(),
                  operation: form.operation.trim(),
                  ...(form.classification === "" ? {} : { classification: form.classification }),
                  ...(form.purpose.trim() === "" ? {} : { purpose: form.purpose.trim() }),
                  ...(form.country.trim() === "" ? {} : { country: form.country.trim() }),
                  requires_multifactor: form.requires_multifactor,
                },
                { correlationId, idempotent: true },
              ),
            "Drafting a policy",
            `Drafted ${form.key.trim()}. It is not evaluated until it is activated.`,
            refusal,
          )
            .then((result) => {
              onDrafted(result);
              if (result.tone === "ok") setForm(EMPTY_DRAFT);
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <div className="nx-field">
          <label htmlFor="policy-key">Key</label>
          <input
            id="policy-key"
            className="nx-input nx-mono"
            required
            minLength={3}
            maxLength={128}
            pattern="[a-z][a-z0-9_.\-]{2,127}"
            aria-describedby="policy-key-hint"
            value={form.key}
            onChange={(event) => {
              setForm({ ...form, key: event.target.value });
            }}
          />
          <span className="nx-hint" id="policy-key-hint">
            Stable across versions. Drafting a key that exists drafts its next version; activating
            that supersedes the version in force.
          </span>
        </div>
        <fieldset className="nx-field" style={{ border: 0, padding: 0, margin: "0 0 12px" }}>
          <legend>Effect</legend>
          <label style={{ marginRight: 16 }}>
            <input
              type="radio"
              name="policy-effect"
              value="allow"
              checked={form.effect === "allow"}
              onChange={() => {
                setForm({ ...form, effect: "allow" });
              }}
            />{" "}
            Allow
          </label>
          <label>
            <input
              type="radio"
              name="policy-effect"
              value="deny"
              checked={form.effect === "deny"}
              onChange={() => {
                setForm({ ...form, effect: "deny" });
              }}
            />{" "}
            Deny
          </label>
        </fieldset>
        <div className="nx-field">
          <label htmlFor="policy-description">Description</label>
          <input
            id="policy-description"
            className="nx-input"
            required
            maxLength={512}
            value={form.description}
            onChange={(event) => {
              setForm({ ...form, description: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="policy-resource-type">Resource type</label>
          <input
            id="policy-resource-type"
            className="nx-input nx-mono"
            required
            maxLength={128}
            pattern="[a-z][a-z0-9_.]{1,127}"
            placeholder="core.consent"
            value={form.resource_type}
            onChange={(event) => {
              setForm({ ...form, resource_type: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="policy-operation">Operation</label>
          <input
            id="policy-operation"
            className="nx-input nx-mono"
            required
            maxLength={64}
            pattern="[a-z][a-z0-9_.]{1,63}"
            placeholder="resolve"
            value={form.operation}
            onChange={(event) => {
              setForm({ ...form, operation: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="policy-classification">Classification (optional)</label>
          <select
            id="policy-classification"
            className="nx-select"
            value={form.classification}
            onChange={(event) => {
              setForm({ ...form, classification: event.target.value as DataClassification | "" });
            }}
          >
            <option value="">Any classification</option>
            {CLASSIFICATIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className="nx-field">
          <label htmlFor="policy-purpose">Purpose (optional)</label>
          <input
            id="policy-purpose"
            className="nx-input nx-mono"
            maxLength={64}
            value={form.purpose}
            onChange={(event) => {
              setForm({ ...form, purpose: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="policy-country">Country (optional)</label>
          <input
            id="policy-country"
            className="nx-input"
            minLength={2}
            maxLength={2}
            pattern="[A-Z]{2}"
            value={form.country}
            onChange={(event) => {
              setForm({ ...form, country: event.target.value.toUpperCase() });
            }}
          />
          <span className="nx-hint">ISO 3166-1 alpha-2. Left empty, the policy names no jurisdiction.</span>
        </div>
        <div className="nx-field">
          <label>
            <input
              type="checkbox"
              checked={form.requires_multifactor}
              onChange={(event) => {
                setForm({ ...form, requires_multifactor: event.target.checked });
              }}
            />{" "}
            Requires a multifactor sign-in
          </label>
        </div>
        <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
          Draft policy
        </button>
        <p className="nx-hint" style={{ marginTop: 8 }}>
          A policy may not govern administrative surfaces. Nexus refuses any policy naming an{" "}
          <Mono>authorization.</Mono> resource type or a service identity resource: who may
          administer is decided by appointment and role, never by data policy.
        </p>
      </form>
    </Card>
  );
}

// ── the list ──────────────────────────────────────────────────────────────

function Transition({
  policy,
  verb,
  onOutcome,
}: {
  policy: AccessPolicy;
  verb: "activate" | "deactivate";
  onOutcome: (outcome: Outcome) => void;
}) {
  const [busy, setBusy] = useState(false);
  const label = verb === "activate" ? "Activate" : "Deactivate";
  return (
    <button
      type="button"
      className={verb === "activate" ? "nx-button nx-button--primary" : "nx-button"}
      disabled={busy}
      aria-label={`${label} ${policy.key} version ${String(policy.version)}`}
      onClick={() => {
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<AccessPolicy>(
              `/authorization/access-policies/${policy.policy_id}/${verb === "activate" ? "activation" : "deactivation"}`,
              {},
              { correlationId, idempotent: true },
            ),
          verb === "activate" ? "Activating a policy" : "Deactivating a policy",
          verb === "activate"
            ? `${policy.key} version ${String(policy.version)} is in force, for every holder of ${policy.operation ?? "the operation"} on ${policy.resource_type ?? "the resource type"} in this tenant. Any earlier version of it is superseded.`
            : `${policy.key} version ${String(policy.version)} is no longer in force. Nothing replaces it.`,
          refusal,
        )
          .then(onOutcome)
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      {label}
    </button>
  );
}

function Policies({ onOutcome }: { onOutcome: (outcome: Outcome) => void }) {
  const session = useSession();
  const mayActivate = session.may(ACTIVATE);
  const mayDeactivate = session.may(DEACTIVATE);
  const [status, setStatus] = useState<AccessPolicyStatus | "">("");

  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<AccessPolicy>>("/authorization/access-policies", {
        params: { limit: PAGE, cursor, ...(status === "" ? {} : { status }) },
        signal,
      }),
    [status],
  );
  // A different status is a different collection, not more of the last one.
  const policies = usePagedCollection<AccessPolicy>(readPage, { resetKey: status });

  const changed = (outcome: Outcome) => {
    onOutcome(outcome);
    if (outcome.tone === "ok") policies.refresh();
  };

  return (
    <Card
      title="Access policies"
      actions={
        <div style={{ display: "flex", gap: 8 }}>
          <label className="nx-visually-hidden" htmlFor="policy-status">
            Status
          </label>
          <select
            id="policy-status"
            className="nx-select"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as AccessPolicyStatus | "");
            }}
          >
            <option value="">Every status</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <button type="button" className="nx-button" onClick={policies.refresh}>
            Refresh
          </button>
        </div>
      }
      bodyless
    >
      <Show
        resource={policies.state}
        what="the access policies"
        onRetry={policies.refresh}
        empty={() =>
          policies.items.length === 0 ? (
            <Empty
              title={status === "" ? "No access policies." : `No ${status} policies.`}
              hint="Without an active allow policy, no data at a restricted classification is reachable by anybody in this tenant, whatever roles they hold."
            />
          ) : null
        }
      >
        {() => (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <caption className="nx-visually-hidden">Access policy versions in this tenant</caption>
              <thead>
                <tr>
                  <th scope="col">Policy</th>
                  <th scope="col">Effect</th>
                  <th scope="col">Governs</th>
                  <th scope="col">Classification</th>
                  <th scope="col">Purpose</th>
                  <th scope="col">Country</th>
                  <th scope="col">Status</th>
                  <th scope="col">Activated</th>
                  {mayActivate || mayDeactivate ? <th scope="col">Change</th> : null}
                </tr>
              </thead>
              <tbody>
                {policies.items.map((policy) => (
                  <tr key={policy.policy_id}>
                    <td>
                      <span className="nx-table__primary">
                        <Mono>{policy.key}</Mono> v{policy.version}
                      </span>
                      <span className="nx-table__secondary" style={{ display: "block" }}>
                        {policy.description}
                      </span>
                      {policy.requires_multifactor ? (
                        <span className="nx-hint" style={{ display: "block" }}>
                          Requires multifactor sign-in
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <Pill tone={policy.effect === "deny" ? "bad" : "ok"}>{policy.effect}</Pill>
                    </td>
                    <td>
                      <Mono>{policy.resource_type ?? "—"}</Mono>
                      <span className="nx-table__secondary" style={{ display: "block" }}>
                        {policy.operation ?? "—"}
                      </span>
                    </td>
                    <td>
                      {policy.classification ? (
                        <Classification value={policy.classification} />
                      ) : (
                        <span className="nx-hint">any</span>
                      )}
                    </td>
                    <td>{policy.purpose ?? <span className="nx-hint">any</span>}</td>
                    <td>{policy.country ?? <span className="nx-hint">any</span>}</td>
                    <td>
                      <Pill tone={STATUS_TONE[policy.status] ?? "quiet"}>{policy.status}</Pill>
                    </td>
                    <td>
                      {policy.activated_at ? (
                        <>
                          {when(policy.activated_at)}
                          {policy.activated_by ? (
                            <span className="nx-table__secondary" style={{ display: "block" }}>
                              by <Identifier value={policy.activated_by} />
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="nx-hint">never</span>
                      )}
                    </td>
                    {mayActivate || mayDeactivate ? (
                      <td>
                        {/*
                          Only a draft can be put into force and only an active
                          version can be retired. A superseded version is
                          history; nothing here changes it.
                        */}
                        {policy.status === "draft" && mayActivate ? (
                          <Transition policy={policy} verb="activate" onOutcome={changed} />
                        ) : null}
                        {policy.status === "active" && mayDeactivate ? (
                          <Transition policy={policy} verb="deactivate" onOutcome={changed} />
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Show>
      <More
        collection={policies}
        label="Show more policies"
        hint="This is one page of the policy versions, newest first — not all of them."
        placement="bodyless"
      />
    </Card>
  );
}

// ── the page ──────────────────────────────────────────────────────────────

export function AccessPolicies() {
  const session = useSession();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const mayRead = session.may(READ);
  const mayDraft = session.may(DRAFT);

  return (
    <>
      <Banner tone="wait" title="A policy names no person.">
        It opens — or closes — a kind of data to <strong>every</strong> holder of the matching
        operation in this tenant. Drafts are never evaluated; only an active version is. Where an
        allow and a deny both match, <strong>the deny wins</strong>.
      </Banner>
      <div style={{ display: "grid", gap: 18 }}>
        {mayRead ? (
          <Policies onOutcome={setOutcome} />
        ) : (
          <Card title="Access policies">
            <p className="nx-hint">
              Reading access policies is not permitted for you. It is held by the access policy
              administrator, whom only the root of trust appoints.
            </p>
          </Card>
        )}
        <Result outcome={outcome} />
        {mayDraft ? <DraftPolicy onDrafted={setOutcome} /> : null}
      </div>
    </>
  );
}
